import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Plug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { getScenarioId } from '@/Config/auth/authStore';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { DrupalIcon } from '@/Domain/components/icons/Drupal.icon';

const DRUPAL_INTEGRATION_NAME = 'drupal' as const;
const DRUPAL_QUERY_KEY = [QueryKey.integration.get, DRUPAL_INTEGRATION_NAME] as const;

export interface DrupalIntegrationParams {
  drupal_integration_active: boolean;
  drupal_integration_token: string;
}

export interface DrupalIntegrationResponse {
  name: typeof DRUPAL_INTEGRATION_NAME;
  params: DrupalIntegrationParams;
}

const drupalFormSchema = z.object({
  drupal_integration_active: z.boolean(),
});

type DrupalFormValues = z.infer<typeof drupalFormSchema>;

interface DrupalSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DrupalSheet = ({ open, onOpenChange }: DrupalSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const scenarioId = getScenarioId();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const { data, isLoading } = useQuery<DrupalIntegrationResponse>({
    queryKey: DRUPAL_QUERY_KEY,
    enabled: open,
  });

  const { mutate: patchDrupal, isPending: isSaving } = useMutation<
    DrupalIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof DRUPAL_INTEGRATION_NAME; params: Partial<DrupalIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (responseData, variables) => {
      queryClient.setQueryData(DRUPAL_QUERY_KEY, responseData);
      const name = t('integration:drupal.title');
      toast.success(
        variables.params.drupal_integration_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      toast.error(detail ?? t('integration:drupal.save_failed'));
    },
  });

  const form = useForm<DrupalFormValues>({
    mode: 'onChange',
    resolver: zodResolver(drupalFormSchema),
    defaultValues: {
      drupal_integration_active: false,
    },
  });

  const { control, reset, watch } = form;
  const isActive = watch('drupal_integration_active');

  // Reset the form whenever fresh server data arrives, so toggling the Sheet
  // always reflects the latest backend state.
  useEffect(() => {
    if (data?.params) {
      reset({ drupal_integration_active: data.params.drupal_integration_active });
    }
  }, [data, reset]);

  const token = data?.params.drupal_integration_token ?? '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        tabIndex={undefined}
        data-testid="drupal-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <DrupalIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="drupal-sheet-title">
                  {t('integration:drupal.title')}
                </SheetTitle>
                <IntegrationEnabledBadge enabled={isActive} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="drupal-sheet-close"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integration:drupal.overview')}
            prerequisites={
              t('integration:drupal.prerequisites', { returnObjects: true }) as string[]
            }
            docsHref="https://docs.quickchat.ai/channels/drupal"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="drupal-connect-enable-section"
          >
            <Button
              type="button"
              variant="dark"
              size="sm"
              className="w-full"
              onClick={() => setConnectDialogOpen(true)}
              data-testid="drupal-connect-button"
            >
              <Plug className="size-4" />
              {t('integrations:connect', { name: 'Drupal' })}
            </Button>
          </IntegrationSection>

          <IntegrationSection
            title={t('integrations:section.settings')}
            data-testid="drupal-settings-section"
          >
            <Controller
              control={control}
              name="drupal_integration_active"
              render={({ field }) => (
                <IntegrationToggleField
                  id="drupal_integration_active"
                  label={t('integrations:enable', { name: 'Drupal' })}
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    patchDrupal({
                      name: DRUPAL_INTEGRATION_NAME,
                      params: { drupal_integration_active: checked },
                    });
                  }}
                  disabled={isLoading || isSaving}
                  data-testid="drupal-active-switch"
                />
              )}
            />
          </IntegrationSection>
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'Drupal' })}
          description={t('integration:drupal.connect_description')}
          submitLabel={t('integrations:done')}
          data-testid="drupal-connect-dialog"
        >
          <CopyField
            label={t('integration:drupal.scenario_id.label')}
            value={scenarioId}
            testId="drupal-scenario-id-input"
            copyTestId="drupal-scenario-id-copy"
          />
          <CopyField
            label={t('integration:drupal.token.label')}
            value={token}
            testId="drupal-token-input"
            copyTestId="drupal-token-copy"
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
